import * as React from "react";
import { Avatar, Box, LinearProgress, Typography, Stack } from "@mui/material";
import { useMediaQuery, Button } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ShieldIcon from "@mui/icons-material/Shield";
import GroupsIcon from "@mui/icons-material/Groups";
import { PillarCard } from "./utils/PillarCard";
import { useI18n } from "../i18n/I18nProvider";
import { supabase } from "../logic/client";


type Props = {
  handlePageChange?: (p: string) => void;
  labProgress?: {
    aware: boolean;
    prepare: boolean;
    share: boolean;
  };
  labScore?: {
    lab_score: number | null;
    availability_signal: "High" | "Medium" | "Low" | null;
  } | null;
};

type UserIdentity = {
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
};

function initials(first?: string | null, last?: string | null) {
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "U";
}

const LAB_CATEGORIES = ["aware", "prepare", "share"] as const;
type LabCategory = (typeof LAB_CATEGORIES)[number];

function isLabCategory(value: unknown): value is LabCategory {
  return LAB_CATEGORIES.includes(value as LabCategory);
}

export default function JourneyPillars(props: Props) {
  const { lang } = useI18n();
  const L = React.useCallback(
    (en: string, de: string) => (lang === "de" ? de : en),
    [lang]
  );

  const [progress, setProgress] = React.useState({
    profile: false,
    aware: false,
    prepare: false,
    share: false,
  });
  const [identity, setIdentity] = React.useState<UserIdentity | null>(null);
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null);

  //const theme = useTheme();
  const isTablet = useMediaQuery("(min-width:600px) and (max-width:1024px)");
  const isDesktop = useMediaQuery("(min-width:1025px)");
  const allDone = progress.aware && progress.prepare && progress.share;
  const handleContinue = () => {
    props.handlePageChange?.("MeetingRoom");
  };

  React.useEffect(() => {
    let mounted = true;

    const loadProgress = async () => {
      try {
        const { data: profile } = await supabase
          .from("employees")
          .select("first_name, last_name, position, age, location, gender, avatar_url")
          .single();

        if (!mounted) return;

        setIdentity({
          first_name: profile?.first_name ?? null,
          last_name: profile?.last_name ?? null,
          avatar_url: profile?.avatar_url ?? null,
        });

        if (profile?.avatar_url) {
          const { data: signed } = await supabase.storage
            .from("avatars")
            .createSignedUrl(profile.avatar_url, 3600);

          if (mounted) setAvatarUrl(signed?.signedUrl ?? null);
        } else {
          if (mounted) setAvatarUrl(null);
        }

        const [{ data: questions }, { data: answers }] = await Promise.all([
          supabase
            .from("vibe_questions_v2")
            .select("id, category")
            .in("category", [...LAB_CATEGORIES]),
          supabase
          .from("employee_vibe_answers_v2")
            .select("question_id"),
        ]);

        const totals: Record<LabCategory, number> = {
          aware: 0,
          prepare: 0,
          share: 0,
        };
        const answered: Record<LabCategory, number> = {
          aware: 0,
          prepare: 0,
          share: 0,
        };
        const categoryByQuestion = new Map<string, LabCategory>();

        (questions ?? []).forEach((question: any) => {
          const category = question.category;
          if (!question.id || !isLabCategory(category)) return;

          categoryByQuestion.set(question.id, category);
          totals[category] += 1;
        });

        const answeredQuestionIds = new Set(
          (answers ?? [])
            .map((answer: any) => answer.question_id)
            .filter((questionId: unknown): questionId is string => typeof questionId === "string")
        );

        answeredQuestionIds.forEach((questionId) => {
          const category = categoryByQuestion.get(questionId);
          if (!category) return;

          answered[category] += 1;
        });

        const hasCompleted = (category: LabCategory) =>
          totals[category] > 0 && answered[category] >= totals[category];

        if (!mounted) return;

        setProgress({
          profile:
            !!profile?.first_name &&
            !!profile?.last_name &&
            !!profile?.age &&
            !!profile?.gender &&
            !!profile?.position &&
            !!profile?.location,
          aware: hasCompleted("aware") || Boolean(props.labProgress?.aware),
          prepare: hasCompleted("prepare") || Boolean(props.labProgress?.prepare),
          share: hasCompleted("share") || Boolean(props.labProgress?.share),
        });
      } catch (err) {
        console.error("Failed to load progress", err);
      }
    };

    loadProgress();

    return () => {
      mounted = false;
    };
  }, [props.labProgress]);

  const isAwareEnabled = progress.profile;
  const isPrepareEnabled = progress.aware;
  const isShareEnabled = progress.prepare;
  const isProfileActive = !progress.profile;
  const scoreValue = Math.min(100, Math.max(0, Number(props.labScore?.lab_score ?? 0)));

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "calc(100vh - 40px)",
        height: isDesktop ? "calc(100vh - 40px)" : "auto",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: isDesktop ? "space-evenly" : isTablet ? "center" : "flex-start",
        boxSizing: "border-box",
        px: { xs: 2, sm: 4, md: 4 },
        py: { xs: 3, sm: 5, md: 3 },
        pb: { xs: 5, sm: 5, md: 3 },
        overflow: isDesktop ? "hidden" : "visible",
        position: "relative",
      }}
    >
      {/* YOU + SCORE */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems="center"
        justifyContent="center"
        spacing={{ xs: 1.5, sm: 3 }}
        mb={{ xs: 1.5, sm: 2, md: 2.5 }}
        sx={{ width: "100%", maxWidth: 680 }}
      >
        <Stack alignItems="center" spacing={0.75}>
          <Box
            onClick={() =>
              props.handlePageChange?.(
                progress.profile ? "Profile" : "ProfileSetup"
              )
            }
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.25s ease",
              background: progress.profile
                ? "rgba(255,255,255,0.05)"
                : "rgba(255,0,0,0.25)",
              boxShadow: isProfileActive
                ? "0 0 30px red"
                : "0 0 10px rgba(255,255,255,0.2)",
              "&:hover": {
                transform: "scale(1.08)",
              },
            }}
          >
            <Avatar
              src={avatarUrl ?? undefined}
              sx={{
                width: "100%",
                height: "100%",
                bgcolor: "rgba(255,0,0,0.18)",
                color: "#fff",
                fontSize: 26,
                fontWeight: 950,
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
              }}
            >
              {initials(identity?.first_name, identity?.last_name)}
            </Avatar>
          </Box>

          <Typography
            sx={{
              color: "white",
              fontWeight: 600,
              letterSpacing: 2,
            }}
          >
            {L("YOU", "DU")}
          </Typography>
        </Stack>

        <Box
          sx={{
            width: { xs: "min(100%, 260px)", sm: 260 },
            px: 2,
            py: 1.5,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.14)",
            boxShadow: "0 0 18px rgba(255,255,255,0.08)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
            <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 900 }}>
              Lab Score
            </Typography>
            <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 900 }}>
              {scoreValue.toFixed(1)}/100
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={scoreValue}
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: "rgba(255,255,255,0.16)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 999,
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
              },
            }}
          />
          <Typography sx={{ color: "rgba(255,255,255,0.62)", mt: 0.75, fontSize: 12, fontWeight: 700 }}>
            {props.labScore?.availability_signal
              ? `${L("Availability", "Verfügbarkeit")}: ${props.labScore.availability_signal}`
              : L("Availability pending", "Verfügbarkeit ausstehend")}
          </Typography>
        </Box>
      </Stack>

      {/* CONNECTION LINE */}
      <Box
        sx={{
          width: 2,
          height: { xs: 18, sm: 20, md: 24 },
          background: "linear-gradient(to bottom, red, transparent)",
          mb: { xs: 1.5, sm: 2, md: 2.5 },
        }}
      />

      {/* PILLARS */}
      <Stack
        direction={isTablet ? "column" : { xs: "column", sm: "row", md: "row" }}
        spacing={isTablet ? 2.5 : { xs: 1.75, sm: 2, md: 3 }}
        alignItems="center"
        justifyContent="center"
        sx={{
          width: "100%",
          maxWidth: isTablet ? 620 : { xs: 440, sm: 900, md: 1200 },
          mx: "auto",
          flex: isTablet ? "none" : 1,
        }}
      >
        <PillarCard
          isTablet={isTablet}
          title={L("AWARE", "BEWUSST")}
          color="#ff2d2d"
          icon={<VisibilityIcon sx={{ color: "#ff2d2d" }} />}
          items={[
            L("Self Reflection", "Selbstreflexion"),
            L("Understand Threat", "Bedrohungen verstehen"),
          ]}
          enabled={isAwareEnabled}
          active={!progress.aware && isAwareEnabled}
          completed={progress.aware}
          statusLabel={
            progress.aware
              ? L("Completed", "Abgeschlossen")
              : isAwareEnabled
                ? L("Active", "Aktiv")
                : L("Locked", "Gesperrt")
          }
          onClick={() =>
            isAwareEnabled &&
            props.handlePageChange?.(
              progress.aware ? "AwareBaselineScoreScreen" : "AwareIntroScreen"
            )
          }
        />

        <PillarCard
          isTablet={isTablet}
          title={L("PREPARE", "BEREIT")}
          color="#ff7a00"
          icon={<ShieldIcon sx={{ color: "#ff7a00" }} />}
          items={[
            L("Action Options", "Handlungsoptionen"),
            L("Decision & Action Plan", "Entscheidungs- & Aktionsplan"),
          ]}
          enabled={isPrepareEnabled}
          active={!progress.prepare && isPrepareEnabled}
          completed={progress.prepare}
          statusLabel={
            progress.prepare
              ? L("Completed", "Abgeschlossen")
              : isPrepareEnabled
                ? L("Active", "Aktiv")
                : L("Locked", "Gesperrt")
          }
          onClick={() =>
            isPrepareEnabled &&
            props.handlePageChange?.(
              progress.prepare ? "PrepareResultScreen" : "PrepareScenarioM1"
            )
          }
        />

        <PillarCard
          isTablet={isTablet}
          title={L("SHARE", "GEMEINSAM")}
          color="#7b61ff"
          icon={<GroupsIcon sx={{ color: "#7b61ff" }} />}
          items={[
            L("Community & Environment", "Gemeinschaft & Umfeld"),
          ]}
          enabled={isShareEnabled}
          active={!progress.share && isShareEnabled}
          completed={progress.share}
          statusLabel={
            progress.share
              ? L("Completed", "Abgeschlossen")
              : isShareEnabled
                ? L("Active", "Aktiv")
                : L("Locked", "Gesperrt")
          }
          onClick={() =>
            isShareEnabled &&
            props.handlePageChange?.(
              progress.share ? "ShareCompletedScreen" : "VibeQuestions:share"
            )
          }
        />
      </Stack>

      {/* MEETING ROOM BUTTON */}
      {allDone && (
        <Box
          sx={{
            width: "100%",
            maxWidth: isTablet ? 760 : 1200,
            mt: { xs: 2, sm: 2.5, md: 3 },
            mb: { xs: 0.5, sm: 1 },
            display: "flex",
            justifyContent: "center",
            flex: "0 0 auto",
          }}
        >
          <Button
            endIcon={<ArrowForwardIcon />}
            onClick={handleContinue}
            sx={{
              px: { xs: 2.5, sm: 3.5 },
              py: 1.25,
              minWidth: { xs: "min(100%, 260px)", sm: 280 },
              borderRadius: 2,
              color: "white",
              fontSize: { xs: "0.95rem", sm: "1rem" },
              fontWeight: 900,
              textTransform: "none",
              letterSpacing: 0,
              background:
                "linear-gradient(90deg, rgba(130,0,10,0.95), rgba(214,41,62,0.95))",
              border: "1px solid rgba(255,255,255,0.16)",
              boxShadow: "0 12px 30px rgba(181,13,32,0.28)",
              "&:hover": {
                background:
                  "linear-gradient(90deg, rgba(102,0,8,0.98), rgba(184,28,46,0.98))",
                boxShadow: "0 14px 34px rgba(181,13,32,0.36)",
              },
            }}
          >
            {L("Go to Meeting Room", "Zum Meeting Room")}
          </Button>
        </Box>
      )}
    </Box>
  );
};
